import React from "react";
import GenericModal from "./GenericModal";
import useModalStore from "../../stores/modalStore";
import {
    ResponsiveContainer,
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
} from "recharts";

const COLORS = ["#8884d8", "#82ca9d", "#ff7300", "#d84888", "#00bcd4", "#ffc658"];

const ComparisonGraphModal = ({ series }) => {
    const show = useModalStore(state => state.showComparisonGraphModal);
    const closeModal = useModalStore(state => state.closeModal);

    if (!show || !series || series.length === 0) return null;

    // Merge all series into one dataset keyed by xKey (period_end date)
    // Each series: { label, data: [{ xKey: date, yKey: value }], xKey, yKey, isDollars }
    const mergedMap = {};
    series.forEach((s) => {
        s.data.forEach((point) => {
            const x = point[s.xKey];
            if (!mergedMap[x]) mergedMap[x] = { period: x };
            mergedMap[x][s.label] = point[s.yKey];
        });
    });

    const mergedData = Object.values(mergedMap).sort(
        (a, b) => new Date(a.period) - new Date(b.period)
    );

    // Determine if we need dual axes: split into dollar vs non-dollar groups
    const dollarSeries = series.filter(s => s.isDollars);
    const nonDollarSeries = series.filter(s => !s.isDollars);
    const useDualAxes = dollarSeries.length > 0 && nonDollarSeries.length > 0;

    const getAxisId = (s) => {
        if (!useDualAxes) return "left";
        return s.isDollars ? "left" : "right";
    };

    const leftLabel = useDualAxes
        ? dollarSeries.map(s => s.label).join(", ")
        : "";
    const rightLabel = useDualAxes
        ? nonDollarSeries.map(s => s.label).join(", ")
        : "";

    return (
        <GenericModal
            name="comparisonGraphModal"
            title="Compare Metrics"
            hideButton={true}
            modalMaxWidth="max-w-4xl"
            stickyFooter={
                <div className="flex justify-end">
                    <button
                        onClick={() => closeModal("comparisonGraphModal")}
                        className="!px-4 !py-2 !text-sm !font-medium !text-gray-600 !bg-transparent !border !border-gray-200 !rounded-xl hover:!bg-gray-50 !transition-colors"
                    >
                        Close
                    </button>
                </div>
            }
        >
            <div className="w-full h-[450px]">
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={mergedData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                            dataKey="period"
                            tick={{ fontSize: 11 }}
                            angle={-30}
                            textAnchor="end"
                            height={60}
                        />
                        <YAxis
                            yAxisId="left"
                            tick={{ fontSize: 11 }}
                            label={useDualAxes ? { value: leftLabel, angle: -90, position: "insideLeft", style: { fontSize: 10 } } : undefined}
                        />
                        {useDualAxes && (
                            <YAxis
                                yAxisId="right"
                                orientation="right"
                                tick={{ fontSize: 11 }}
                                label={{ value: rightLabel, angle: 90, position: "insideRight", style: { fontSize: 10 } }}
                            />
                        )}
                        <Tooltip
                            formatter={(value, name) => {
                                const s = series.find(s => s.label === name);
                                if (s?.isDollars) return [`$${Number(value).toFixed(2)}`, name];
                                return [Number(value).toFixed(2), name];
                            }}
                        />
                        <Legend />
                        {series.map((s, i) => (
                            <Line
                                key={s.label}
                                yAxisId={getAxisId(s)}
                                type="monotone"
                                dataKey={s.label}
                                stroke={COLORS[i % COLORS.length]}
                                dot
                                strokeWidth={2}
                            />
                        ))}
                    </LineChart>
                </ResponsiveContainer>
            </div>
        </GenericModal>
    );
};

export default ComparisonGraphModal;
